import React, { useMemo } from 'react';
import styled from 'styled-components/macro';

import formatNumber from 'helpers/formatNumber';
import { ABBREVIATION_THRESHOLD } from 'helpers/abbreviateNumber';

import useSocialTableData from './utils/useSocialTableData';

import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Table from 'components/data-visualization/Table/Table';
import Layout from 'components/styled/Layout/Layout';
import BreakdownTablePlaceholder from 'components/data-visualization/Table/BreakdownTablePlaceholder';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

import { ReactComponent as FacebookIcon } from 'icons/social/facebook.svg';
import { ReactComponent as TwitterIcon } from 'icons/social/twitter.svg';
import { ReactComponent as InstagramIcon } from 'icons/social/instagram.svg';
import { IMetric } from 'maps/metrics';
import {
  TableRow,
  TableHeader,
  HorizontalScrollContainer,
} from 'components/Overview/BreakdownTable/styles';
import useMedia from 'hooks/useMedia';
import { IBreakdown, ISocialData } from './types';
import { HeaderWrapper } from 'components/data-visualization/Table/DropdownHead';

const SocialCellLayout = styled.div`
  display: flex;
  align-items: center;

  svg {
    width: 1.5em;
    height: 1.5em;
  }

  span:first-of-type {
    margin: 0 ${(p): string => p.theme.spacing.xsmall};
    color: ${(p): string => p.theme.Blue};
    font-weight: 600;
  }
`;

const socialIcons: { [key: string]: any } = {
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  instagram: InstagramIcon,
};

const followersMetric = {
  value: 'countRtd',
  key: 'countRtd',
  label: '',
};

interface IMetricExtended extends IMetric {
  label: string;
}

interface ISocialTable {
  sortMetric: IMetricExtended;
  isPending: boolean;
  headers: string[];
  socialData: ISocialData[];
}

/**
 * Renders the latest follower info of all known social platforms
 * plus the top ranking social media post
 * @param {array} headers The header titles tha are rendered for the social platform table
 * @param {object} socialData The latest follower information
 * @param {bool} isPending Indicates whether the loading state is active
 */
const SocialTable: React.FC<ISocialTable> = (props) => {
  const { headers, socialData, isPending, sortMetric } = props;

  const breakdowns = useSocialTableData(socialData, followersMetric.value);

  const isLargeScreen = useMedia('(min-width: 800px)');

  const hasNoData = useMemo(() => {
    if (Array.isArray(breakdowns)) {
      return breakdowns.length === 0;
    }
    return !isPending;
  }, [breakdowns, isPending]);

  return (
    <HorizontalScrollContainer>
      <AnimatedLoadingWrapper loading={isPending}>
        <Layout.Relative>
          <Table>
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <TableHeader key={header} isRightAligned={index === 1}>
                    <HeaderWrapper
                      condition={header
                        .toLowerCase()
                        .includes('this week to date')}
                    >
                      {header}
                    </HeaderWrapper>
                  </TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {!breakdowns && isPending && (
                <TableLoader columnCount={headers.length} />
              )}
              {hasNoData && !isPending && (
                <BreakdownTablePlaceholder
                  isSmallScreen={!isLargeScreen}
                  columnCount={headers.length}
                />
              )}
              {breakdowns &&
                breakdowns.map((breakdown: IBreakdown): JSX.Element | null => {
                  const { platformName } = breakdown;
                  const metricValue = breakdown[sortMetric.value];
                  const SocialIcon =
                    socialIcons[(platformName as string).toLowerCase()]?.main;
                  const value = formatNumber(metricValue, 'number');
                  const abbreviatedValue = formatNumber(
                    metricValue,
                    'number',
                    true
                  );
                  const hasTooltip =
                    Math.abs(metricValue) >= ABBREVIATION_THRESHOLD;
                  const isPositive = metricValue > 0;

                  return SocialIcon ? ( // TEMPORARY... quick fix to hide new social networks
                    <TableRow
                      key={(breakdown as IBreakdown).metricName}
                      isRightAligned
                    >
                      <td>
                        <SocialCellLayout>
                          <SocialIcon />
                          <TippyToolTip
                            enabled={hasTooltip}
                            canBeCopied
                            tooltipContent={value}
                          >
                            {isPositive && '+'}
                            {abbreviatedValue}
                          </TippyToolTip>
                          {(breakdown as IBreakdown).metricName}
                        </SocialCellLayout>
                      </td>
                      <DoubleMetricCell
                        entityData={breakdown}
                        sortBy={followersMetric}
                        header={followersMetric}
                        hideSecondary
                      />
                    </TableRow>
                  ) : null;
                })}
            </tbody>
          </Table>
        </Layout.Relative>
      </AnimatedLoadingWrapper>
    </HorizontalScrollContainer>
  );
};

export default SocialTable;
